import { useCallback } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { ViewHeader } from './DashboardLayout';

/**
 * Dashboard → Team (M16 T2).
 *
 * Admin-only view of the portal's users with their role overlay. HubSpot Users
 * API is the directory of who EXISTS; tenant_permissions (DO) is the role
 * overlay, LEFT JOINed server-side. Each row has a role dropdown
 * (Admin/Operator/Viewer); changing it POSTs to /api/admin/team/set-role.
 *
 * Requires the `settings.users.read` scope. If missing (existing installs
 * pre-M16), the endpoint returns { needsReauth: true } and this view shows a
 * re-auth prompt instead of the table.
 *
 * Rules:
 *   - Viewer = no role row (default). Promoting a viewer INSERTs a row.
 *   - You can't demote/remove yourself (the last-admin guard is server-side,
 *     but we disable the self-row's dropdown for clarity).
 *   - The acting admin is flagged isSelf.
 *
 * Reference: src/handlers/adminActions.ts (handleTeam, handleTeamSetRole)
 */

type Role = 'admin' | 'operator' | 'viewer';

interface TeamUser {
  userId: number;
  email: string;
  name: string;
  role: Role;
  isSelf: boolean;
}

interface TeamResponse {
  users: TeamUser[];
  hasMore: boolean;
  needsReauth?: boolean;
}

export function Team() {
  const fetcher = useCallback(
    (signal: AbortSignal) => api.get<TeamResponse>('admin/team', { signal }),
    []
  );
  const { data, loading, error, refetch } = useAsync(fetcher, []);

  return (
    <>
      <ViewHeader
        title="Team"
        subtitle="Grant users the Operator or Admin role. Viewers can see the card but can't act."
      />

      {loading && <p className="text-sm text-ink-500">Loading team…</p>}

      {error && (
        <p className="text-sm text-warn-600">
          Failed to load team: {error.message}
        </p>
      )}

      {data?.needsReauth && (
        <div className="rounded-lg border border-warn-200 bg-warn-50 p-4">
          <p className="text-sm font-medium text-warn-800">
            Additional permissions required
          </p>
          <p className="mt-1 text-sm text-warn-700">
            The Team tab needs the <code>settings.users.read</code> permission,
            which wasn&rsquo;t granted when this account was connected.
          </p>
          <a
            href="/api/auth/login-url"
            className="mt-3 inline-block rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-700"
          >
            Re-connect with HubSpot
          </a>
        </div>
      )}

      {data && !data.needsReauth && (
        <TeamTable users={data.users} hasMore={data.hasMore} onChanged={refetch} />
      )}
    </>
  );
}

function TeamTable({
  users,
  hasMore,
  onChanged,
}: {
  users: TeamUser[];
  hasMore: boolean;
  onChanged: () => void;
}) {
  return (
    <>
      <div className="overflow-hidden rounded-lg border border-ink-200">
        <table className="min-w-full divide-y divide-ink-200 text-sm">
          <thead className="bg-ink-50 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {users.map((u) => (
              <TeamRow key={u.userId} user={u} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <p className="mt-2 text-xs text-ink-400">
          Showing the first 500 users. Use HubSpot to manage larger teams.
        </p>
      )}
    </>
  );
}

function TeamRow({ user, onChanged }: { user: TeamUser; onChanged: () => void; }) {
  const change = async (role: Role) => {
    if (role === user.role) return;
    try {
      await api.post('admin/team/set-role', {
        userId: user.userId,
        userEmail: user.email,
        role,
      });
      onChanged();
    } catch (err) {
      // The last-admin guard returns 409; surface the message.
      const msg = err instanceof Error ? err.message : 'Failed to update role';
      alert(msg);
    }
  };

  return (
    <tr className={user.isSelf ? 'bg-brand-50/40' : undefined}>
      <td className="px-4 py-3">
        <span className="font-medium text-ink-800">{user.name}</span>
        {user.isSelf && (
          <span className="ml-2 text-xs text-ink-400">(you)</span>
        )}
      </td>
      <td className="px-4 py-3 text-ink-600">{user.email}</td>
      <td className="px-4 py-3">
        {/* Self can't be demoted via this dropdown (the last-admin guard is
            server-side, but disabling the self row avoids a confusing 409). */}
        <select
          value={user.role}
          disabled={user.isSelf}
          onChange={(e) => change(e.target.value as Role)}
          className="rounded-md border border-ink-200 bg-white px-2 py-1 text-xs text-ink-700 focus:border-brand-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-400"
          title={user.isSelf ? "You can't change your own role here" : undefined}
        >
          <option value="admin">Admin</option>
          <option value="operator">Operator</option>
          <option value="viewer">Viewer</option>
        </select>
      </td>
    </tr>
  );
}
